/* ============================================================
   TMS Wishlist — localStorage-backed favourites.
   Components:
   - TmsWishlistStore: shared singleton wrapping localStorage
   - <tms-wishlist-button>: heart toggle on PDP / cards / page
   - <tms-wishlist-count>: header badge that reflects current count
   - <tms-wishlist-grid>: renders saved products on /pages/wishlist
   Cross-component sync via storage events + custom 'tms-wishlist:change'.
============================================================ */

const TmsWishlistStore = (() => {
  const KEY = 'tms_wishlist_v1';
  const EVENT = 'tms-wishlist:change';

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) {
      return [];
    }
  };

  const write = (handles) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(handles));
    } catch (e) {
      // quota or storage disabled — silently ignore
    }
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { handles } }));
  };

  return {
    get: () => read(),
    has: (handle) => read().includes(handle),
    add: (handle) => {
      if (!handle) return;
      const current = read();
      if (current.includes(handle)) return;
      write([handle, ...current]);
    },
    remove: (handle) => {
      const current = read();
      const next = current.filter((h) => h !== handle);
      if (next.length === current.length) return;
      write(next);
    },
    toggle: (handle) => {
      if (!handle) return false;
      const current = read();
      const isIn = current.includes(handle);
      write(isIn ? current.filter((h) => h !== handle) : [handle, ...current]);
      return !isIn;
    },
    count: () => read().length,
    subscribe: (callback) => {
      const handler = (e) => callback(e.detail?.handles ?? read());
      window.addEventListener(EVENT, handler);
      const storageHandler = (e) => {
        if (e.key === KEY) callback(read());
      };
      window.addEventListener('storage', storageHandler);
      return () => {
        window.removeEventListener(EVENT, handler);
        window.removeEventListener('storage', storageHandler);
      };
    },
    EVENT,
    KEY
  };
})();

if (typeof window !== 'undefined') {
  window.TmsWishlistStore = TmsWishlistStore;
}

class TmsWishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    if (!this.handle) return;

    this.button = this.querySelector('button') || this;
    this.unsubscribe = TmsWishlistStore.subscribe(() => this.render());
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  render() {
    const active = TmsWishlistStore.has(this.handle);
    this.classList.toggle('is-active', active);
    this.button.setAttribute('aria-pressed', String(active));
    const label = active
      ? this.dataset.labelRemove || 'Remove from wishlist'
      : this.dataset.labelAdd || 'Add to wishlist';
    this.button.setAttribute('aria-label', `${label}`);
    const textNode = this.querySelector('[data-wishlist-text]');
    if (textNode) textNode.textContent = label;
  }
}

if (!customElements.get('tms-wishlist-button')) {
  customElements.define('tms-wishlist-button', TmsWishlistButton);
}

/* Document-level capture-phase click handler so the wishlist toggle wins over
   the product card's full-card title-link overlay (.card__heading a::after
   sits at z-index:1 above default-stacked siblings and otherwise steals the
   click, navigating to the PDP instead). */
document.addEventListener(
  'click',
  (event) => {
    let trigger = event.target.closest('tms-wishlist-button');

    // Only look beneath the click point when the click landed on a product card: anything
    // layered over a card (drawer, modal, mobile menu) must keep its own clicks.
    const card = event.target.closest('.card-wrapper');
    if (!trigger && card && typeof document.elementsFromPoint === 'function') {
      const stack = document.elementsFromPoint(event.clientX, event.clientY);
      for (const el of stack) {
        const found = el.closest && el.closest('tms-wishlist-button');
        if (found && card.contains(found)) {
          trigger = found;
          break;
        }
      }
    }

    if (!trigger) return;
    const handle = trigger.dataset.handle;
    if (!handle) return;

    event.preventDefault();
    event.stopPropagation();

    const wasActive = TmsWishlistStore.has(handle);
    TmsWishlistStore.toggle(handle);

    // Twitter-style burst only when adding (not when removing)
    if (!wasActive) {
      trigger.classList.remove('is-bursting');
      // Force reflow so the same class can re-trigger the animation on rapid clicks
      void trigger.offsetWidth;
      trigger.classList.add('is-bursting');
      window.setTimeout(() => trigger.classList.remove('is-bursting'), 700);
    }
  },
  true
);

class TmsWishlistCount extends HTMLElement {
  connectedCallback() {
    this.unsubscribe = TmsWishlistStore.subscribe(() => this.render());
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  render() {
    const count = TmsWishlistStore.count();
    this.textContent = count > 99 ? '99+' : String(count);
    this.classList.toggle('is-empty', count === 0);
  }
}

if (!customElements.get('tms-wishlist-count')) {
  customElements.define('tms-wishlist-count', TmsWishlistCount);
}

class TmsWishlistGrid extends HTMLElement {
  static FETCH_TIMEOUT_MS = 6000;

  connectedCallback() {
    this.list = this.querySelector('[data-wishlist-list]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.cardTemplate = this.querySelector('[data-wishlist-card-template]');
    this.currency = this.dataset.currency || 'AUD';
    this.locale = this.dataset.locale || 'en-AU';

    this.unsubscribe = TmsWishlistStore.subscribe(() => this.render());
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  async render() {
    const handles = TmsWishlistStore.get();

    if (handles.length === 0) {
      this.list.replaceChildren();
      this.empty?.removeAttribute('hidden');
      return;
    }

    this.empty?.setAttribute('hidden', '');

    const products = await Promise.all(handles.map((h) => this.fetchProduct(h)));
    const items = products
      .map((product, index) => ({ product, handle: handles[index] }))
      .filter((item) => item.product);

    this.list.replaceChildren();
    items.forEach((item) => this.appendCard(item.product, item.handle));
  }

  async fetchProduct(handle) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TmsWishlistGrid.FETCH_TIMEOUT_MS);
    try {
      const resp = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!resp.ok) return null;
      return await resp.json();
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  appendCard(product, handle) {
    const node = this.cardTemplate.content.firstElementChild.cloneNode(true);

    const link = node.querySelector('[data-card-link]');
    link.href = product.url;
    link.setAttribute('aria-label', product.title);

    const imageEl = node.querySelector('[data-card-image]');
    if (product.featured_image) {
      const img = document.createElement('img');
      img.src = this.resizeImage(product.featured_image, 600);
      img.loading = 'lazy';
      img.alt = product.title;
      img.width = 600;
      img.height = 600;
      imageEl.appendChild(img);
    } else {
      imageEl.classList.add('tms-wishlist-card__image--placeholder');
    }

    node.querySelector('[data-card-title]').textContent = product.title;
    node.querySelector('[data-card-price]').textContent = this.formatMoney(product.price);

    const removeBtn = node.querySelector('[data-card-remove]');
    if (removeBtn) {
      removeBtn.setAttribute('aria-label', `${removeBtn.dataset.labelPrefix || 'Remove'} ${product.title}`);
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        TmsWishlistStore.remove(handle);
      });
    }

    this.list.appendChild(node);
  }

  resizeImage(url, width) {
    if (!url) return '';
    return url.replace(/(\.(?:jpg|jpeg|png|webp|gif))(\?|$)/i, `_${width}x$1$2`);
  }

  formatMoney(cents) {
    const value = (parseInt(cents, 10) || 0) / 100;
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2
      }).format(value);
    } catch (e) {
      return `$${value.toFixed(2)}`;
    }
  }
}

if (!customElements.get('tms-wishlist-grid')) {
  customElements.define('tms-wishlist-grid', TmsWishlistGrid);
}
